import { useEffect, useState } from "react";
import { CalendarDays, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const todayIso = () => new Date().toISOString().slice(0, 10);

export function StaffReservationDialog({ open, onOpenChange, onCreated, initialDate }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", date: initialDate || todayIso(), time: "", party_size: 2, notes: "" });
  const [avail, setAvail] = useState(null);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { if (open && initialDate) setForm((f) => ({ ...f, date: initialDate })); }, [open, initialDate]);
  useEffect(() => {
    if (!open) return;
    setAvail(null);
    api.get(`/reservations/availability?date=${form.date}&party=${Math.max(1, Number(form.party_size) || 1)}`)
      .then((r) => { setAvail(r.data); setForm((f) => ({ ...f, time: "" })); })
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); setAvail({ times: [] }); });
  }, [open, form.date, form.party_size]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Inserisci il nome"); return; }
    if (!form.time) { toast.error("Scegli l'orario"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/reservations", { ...form, party_size: Math.max(1, Number(form.party_size) || 1) });
      toast.success(`Tavolo prenotato · ${data.time} · ${data.name} (${data.party_size} pers.)`);
      setForm({ name: "", phone: "", email: "", date: form.date, time: "", party_size: 2, notes: "" });
      onCreated?.(data); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="staff-reservation-dialog">
        <DialogHeader><DialogTitle className="font-display inline-flex items-center gap-2"><CalendarDays className="h-5 w-5 text-[#4a8a56]" />Prenota un tavolo</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-500 -mt-2">Prenotazione presa dal locale: usa gli stessi tavoli e orari del sito, così online restano solo i posti davvero liberi.</p>
        <div className="grid grid-cols-2 gap-2">
          <Input value={form.name} onChange={set("name")} placeholder="Nome *" className="h-10" data-testid="srv-name" />
          <Input value={form.phone} onChange={set("phone")} placeholder="Telefono" className="h-10" data-testid="srv-phone" />
          <Input value={form.email} onChange={set("email")} placeholder="Email (per conferma e promemoria)" className="h-10 col-span-2" data-testid="srv-email" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-[11px] text-gray-400">Data</label><input type="date" min={todayIso()} value={form.date} onChange={set("date")} className="block w-full h-10 rounded-lg border border-gray-200 px-2 text-sm" data-testid="srv-date" /></div>
          <div><label className="text-[11px] text-gray-400 inline-flex items-center gap-1"><Users className="h-3 w-3" />Persone</label><Input type="number" min="1" max="100" value={form.party_size} onChange={set("party_size")} className="h-10" data-testid="srv-party" /></div>
        </div>
        <div>
          <label className="text-[11px] text-gray-400">Orario {avail?.has_tables === false && <span>· nessun tavolo configurato: disponibilità non verificata</span>}</label>
          {avail === null ? <Loader2 className="h-4 w-4 animate-spin text-[#5aa469] mt-2" /> : avail.closed ? <p className="text-sm text-red-600 mt-1" data-testid="srv-closed">Locale chiuso in questa data.</p> : avail.times.length === 0 ? <p className="text-sm text-gray-500 mt-1" data-testid="srv-no-times">Nessun orario{avail.enabled === false ? ": attiva le prenotazioni nelle Impostazioni" : " disponibile"}.</p> : (
            <div className="flex gap-1.5 flex-wrap mt-1" data-testid="srv-times">
              {avail.times.map((s) => {
                const sel = form.time === s.t;
                return <button key={s.t} disabled={!s.fits} onClick={() => setForm((f) => ({ ...f, time: s.t }))} className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${sel ? "bg-[#5aa469] text-white border-[#5aa469]" : !s.fits ? "bg-gray-100 text-gray-400 border-gray-200 line-through" : "bg-white border-gray-200 hover:border-[#5aa469]"}`} data-testid={`srv-time-${s.t}`}>{s.t}{s.seats_left !== null && s.seats_left !== undefined && <span className="opacity-70"> · {Math.max(0, s.seats_left)} posti</span>}</button>;
              })}
            </div>
          )}
        </div>
        <Input value={form.notes} onChange={set("notes")} placeholder="Note (es. seggiolone, compleanno)" className="h-10" data-testid="srv-notes" />
        <Button onClick={submit} disabled={sending} className="w-full rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white" data-testid="srv-submit">{sending ? "Salvo…" : "Conferma prenotazione"}</Button>
      </DialogContent>
    </Dialog>
  );
}
